import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import _ from "lodash";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { Box, Button, Tab, Tabs } from "@mui/material";
import checkAll from "@iconify/icons-mdi/check-all";
import { InlineIcon } from "@iconify/react";
import {
  MyHrContractCard,
  MyHrStickyPersonalDataHeader,
} from "my-hr/components";
import { useMyHrContract, useMyHrEmployee, useMyHrUserRolesDataByContract } from "my-hr/hooks";
import { MyLeaveAllLeavesWithCoverage } from "my-leave/components";
import useStyles from "my-leave/components/filter-bar/filter-bar.style";
import {
  userLeaveRequests,
  userLeaveRequestsIsMobile,
} from "my-leave/configs/table-defs";
import { MY_LEAVE_APPROVE_ALL_REQUESTS } from "my-leave/graphql";
import { useMyLeaveEvents, useMyLeaveUserRequests, useMyLeaveRoleTabs } from "my-leave/hooks";
import {
  OcCard,
  OcDataTable,
  OcPageNameTitle,
  OcPageTransition,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcButtonGroup,
  useOcTableState,
  useOcClientState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcProperty,
  useOcSnackbar,
} from "oc/hooks";
import getSortedList from "oc/utils/get-sorted-list";
import {
  myLeaveCurrentDateState,
  ocRoleFiltersState,
  ocScrollTriggerState,
} from "states";

export const MyLeaveUserRequestsPage = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  let { contract, product, role } = useParams<{
    contract: string;
    product: string;
    role: string;
  }>();
  const { data: contractData } = useMyHrContract({ contract });
  let employeeId = contractData?.employee?.code;
  const isMobile = useOcIsMobile();
  const classes = useStyles({ isMobile, createLeaveRequestIsEnabled: "false" });
  let { contractCode } = useOcCurrentUserState();

  const { hasRole, data: rolesData } = useMyHrUserRolesDataByContract({ contract: contractCode });

  const buttonsDef = ["OC_TO_BE_ASSESSED", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: `MyTimeAdmissionCard`,
    buttonsDef,
  });

  const [loadingState, setLoadingState] = useState(false);

  const { setOpenMessage } = useOcSnackbar();

  // Check if user has the required role from URL
  useEffect(() => {
    if (role && contractCode) {
      const requiredRole = `MyLeave.${role}`;
      if (!hasRole([requiredRole])) {
        const translatedRole = t(`MY_LEAVE_ROLE_${role.toUpperCase()}`);
        setOpenMessage({
          type: "error",
          message: t("MY_LEAVE_NO_PERMISSION_FOR_ROLE_MESSAGE", { role: translatedRole }),
        });
        history.push("/my-leave/dashboard");
      }
    }
  }, [role, contractCode, hasRole, history, setOpenMessage, t]);

  // Available roles with tabs
  const { roles, selectedTab } = useMyLeaveRoleTabs({ rolesData, currentRole: role });

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newRole = roles[newValue]?.key;
    if (newRole && newRole !== role) {
      history.replace(`/my-leave/user-requests/${product}/${newRole}/${contract}`);
    }
  };

  const { data: employeeData } = useMyHrEmployee({ employeeId });

  const roleFilters = useRecoilValue(
    ocRoleFiltersState(contract + product + role),
  );

  const [currentDate, setCurrentDate] = useRecoilState(myLeaveCurrentDateState);

  const pageRef = useRef<HTMLDivElement>(null);

  const [scrollTrigger, setScrollTrigger] = useRecoilState(ocScrollTriggerState);
  const [pageWidth, setPageWidth] = useOcClientState<number>("pageWidth", 0);

  const handleScroll = useCallback(() => {
    const triggerY = 100;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    setScrollTrigger(scrollTop >= triggerY ? "show" : "hide");
  }, [setScrollTrigger]);

  const handleResize = useCallback(() => {
    const width = pageRef.current?.clientWidth;
    if (width && width !== pageWidth) {
      setPageWidth(width);
    }
  }, [pageWidth, setPageWidth]);

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, handleResize]);

  const { data: approveAllLeaveRequestsEnabledData } = useOcProperty({
    resource: "myLeave",
    key: "approveAllLeaveRequestsEnabled",
  });

  const requestsEnabled =
    approveAllLeaveRequestsEnabledData === "false" ? false : true;

  let tableId = "userLeavesTableData";

  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "period",
    pageSize: 5,
  });

  const { data, loading } = useMyLeaveUserRequests({
    contract,
  });

  // Define the type for a leave request item
  type LeaveRequestItem = {
    _id: string;
    contract: string;
    startDate: string;
    endDate: string;
    type: string;
    state: string;
    workDays: number;
    createdAt: string;
    effectiveWorkDays?: number;
    period?: string;
    [key: string]: any;
  };

  let result: LeaveRequestItem[] = _.cloneDeep(data ?? []);

  let newRequestedLeaveDays = 0;

  for (const item of result) {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    };
    let startDate = new Date(item.startDate).toLocaleDateString(
      i18n.language,
      options,
    );
    let endDate = new Date(item.endDate).toLocaleDateString(
      i18n.language,
      options,
    );

    if (item.type === "leave" && item.state === "requested") {
      newRequestedLeaveDays = newRequestedLeaveDays + item.workDays;
    }
    let createdAt = new Date(item.createdAt).toLocaleString(i18n.language);
    item.createdAt = createdAt;
    if (item._id.startsWith("jd-")) {
      if (item.effectiveWorkDays !== undefined && item.effectiveWorkDays > 0) {
        item.createdAt = t(`MY_LEAVE_RECORDED_IN_JDOLBER`);
      } else {
        item.createdAt = t(`MY_LEAVE_CANCELLED_IN_JDOLBER`);
      }
    }
    item.period = `${startDate} - ${endDate}`;
    item.type = t(item.type);
  }

  result.sort(
    getSortedList(
      tableData?.sortDirection === "asc"
        ? tableData?.sortField
        : `-${tableData?.sortField}`,
    ),
  );

  if (selectedButton === "OC_TO_BE_ASSESSED") {
    const endDate = moment().startOf("year").toDate();
    result = result.filter((item) => {
      if (
        item.state === "requested" &&
        new Date(item.endDate) > new Date(endDate)
      ) {
        return true;
      }
      return false;
    });
  }

  let list = {
    rows: result,
    total: result.length,
    page: tableData.page,
    pageSize: tableData.pageSize,
  };

  const handleCurrentDateChange = (date: Date | string) => {
    setCurrentDate(date);
  };

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const originalonSelectRow = (event: LeaveRequestItem) => {
    let { _id, contract } = event.row ?? event;
    history.push(
      `/my-leave/user-request/${product}/${role}/${contract}/${_id}`,
    );
  };

  const onSelectRow = (o: LeaveRequestItem) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? null : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const [approveAllRequests] = useMutation(MY_LEAVE_APPROVE_ALL_REQUESTS, {
    onCompleted: (data) => {
      const results = data?.approveAllMyLeaveRequests;
      let isOK = false;
      results.forEach((result: any) => {
        if (result.result) {
          isOK = true;
        } else {
          setOpenMessage({
            type: "error",
            message: t("OC_ALL_APPROVE_FAILED"),
          });
        }
      });
      if (isOK) {
        setOpenMessage({
          type: "success",
          message: t("MY_TIME_ALL_APPROVE_SUCEEDED"),
        });
      }
      setLoadingState(false);
    },
    onError: () => {
      setOpenMessage({
        type: "error",
        message: t("MY_TIME_APPROVE_FAILED"),
      });
    },
  });

  const handleApproveAllRequests = () => {
    const params = { contractCode: contract };
    setLoadingState(true);
    approveAllRequests({ variables: params });
  };

  const viewedByAdmin = role === "viewer" ? true : false;
  const isAssigner = role === "assigner" ? true : false;

  const {
    loadEvents,
    data: leavesData,
    loading: leaveEventsLoading,
    eventsByDays,
  } = useMyLeaveEvents({
    roleFilters: roleFilters,
    currentDate: moment(currentDate).toDate(),
    viewedByAdmin,
    contract: contract,
    role: role,
  });

  const tableDef = isMobile ? userLeaveRequestsIsMobile : userLeaveRequests;

  const filteredTableDef = {
    id: userLeaveRequests?.id,
    columns: userLeaveRequests?.columns?.filter(
      (column) =>
        !userLeaveRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  useEffect(() => {
    setCurrentDate(new Date());
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const disabled = contract === contractCode;

  const hasRequestedItems =
    Array.isArray(list?.rows) &&
    list.rows.length > 0 &&
    list.rows.some((item) => item.state === "requested");

  return (
    <OcPageTransition>
      <Box ref={pageRef}>
        <MyHrStickyPersonalDataHeader
          pageWidth={pageWidth}
          employee={employeeData}
          contract={contractData}
          trigger={scrollTrigger === "show" ? true : false}
        />
        <OcPageNameTitle title={t("OC_EMPLOYEE_REQUESTS")} goBack={true} />
        {roles.length > 1 && (
          <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
            <Box bgcolor="#E3E5E5">
              <Tabs
                value={selectedTab}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                onChange={handleTabChange}
                aria-label="role tabs"
              >
                {roles.map((r) => (
                  <Tab key={r.key} id={r.key} label={r.label} />
                ))}
              </Tabs>
            </Box>
          </Box>
        )}
        <MyHrContractCard employee={employeeData} contract={contractData} />
        <Box padding={isMobile ? "16px 0px" : "16px"}>
          <OcCard
            label={t("MY_LEAVE_LEAVE_AND_ABSENCE_REQUESTS")}
            loading={loadingState}
          >
            <Box>
              <Box className={classes.root}>
                <Box
                  className={
                    viewedByAdmin && !requestsEnabled
                      ? classes.leftContainerExaminerView
                      : classes.leftContainer
                  }
                />
                <Box
                  style={{
                    justifyContent: isMobile ? "center" : "unset",
                    display: isMobile ? "flex" : "unset",
                  }}
                >
                  <OcButtonGroup />
                </Box>
                {isMobile && <Box height="36px" />}
                <Box className={classes.rightContainer}>
                  {!viewedByAdmin && !isAssigner && requestsEnabled ? (
                    <Button
                      id="all-approve"
                      variant="outlined"
                      color="secondary"
                      startIcon={<InlineIcon icon={checkAll} />}
                      onClick={handleApproveAllRequests}
                      className={classes.allApprove}
                      disableElevation
                      disabled={disabled || !hasRequestedItems}
                    >
                      {t("OC_ALL_APPROVE")}
                    </Button>
                  ) : (
                    <Box>
                      {(viewedByAdmin || isAssigner || !requestsEnabled) && (
                        <Box width="180px" />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
              <OcDataTable
                data={list}
                loading={loading && list?.rows?.length === 0 ? true : false}
                tableDef={tableDef}
                tableData={tableData}
                setTableData={setTableData}
                pagination={true}
                onSelectRow={onSelectRow}
                localPaging={true}
                filteredTableDef={filteredTableDef}
                selectedRowId={selectedRowId ?? undefined}
                handleMoreButton={originalonSelectRow}
              />
            </Box>
          </OcCard>
          <Box height="16px" />
          <MyLeaveAllLeavesWithCoverage
            cardTitle={t("MY_LEAVE_LEAVES_AND_REMOTE_WORKS")}
            currentDate={currentDate}
            onCurrentDateChange={handleCurrentDateChange}
            monthFiltering={false}
            viewedByAdmin={viewedByAdmin}
            leavesData={leavesData}
            loading={leaveEventsLoading}
            eventsByDays={eventsByDays}
          />
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyLeaveUserRequestsPage;
